import { sizes } from '@lmc/common/constants'
import type { LogCategory, LogCategoryItem } from '@lmc/ts-common'
import { CrashIcon, EmptyState, HeaderSection } from '@lmc/ui-kit'
import dayjs from 'dayjs'
import _ from 'lodash'
import qs from 'qs'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { getJSON } from '../../../common/services/api'
import CareDeliveryFilters from '../components/careDelivery/CareDeliveryFilters/CareDeliveryFilters'
import CareDeliveryTable from '../components/careDelivery/CareDeliveryTable/CareDeliveryTable'
import type { Data } from './CareDeliveryScreen.types'

type CareDeliveryScreenProps = {
  categories: LogCategory[]
  items: LogCategoryItem[]
  preferredPeopleNominative: string
}

export function CareDeliveryScreen({
  categories,
  items,
  preferredPeopleNominative
}: CareDeliveryScreenProps): React.ReactElement {
  const [to, setToDate] = React.useState(dayjs())
  const [from, setFromDate] = React.useState(dayjs().subtract(1, 'month'))
  const [isLoading, setIsLoading] = React.useState(true)
  const [data, setData] = React.useState<Data[] | null>(null)
  const [hasError, setHasError] = React.useState(false)
  const [categoryId, setCategoryId] = React.useState<string | null>(null)
  const { t } = useTranslation()
  React.useEffect(() => {
    // Changing a filter fires a new request without cancelling the last one,
    // so a slow rejection could otherwise land after a newer success and hide
    // valid data behind the error panel.
    let isCurrent = true

    setIsLoading(true)
    setHasError(false)
    const query = qs.stringify({
      from: from.format('DD-MM-YYYY'),
      to: to.format('DD-MM-YYYY')
    })
    const url = categoryId
      ? `/api/aggregations/care-delivery/${categoryId}?${query}`
      : `/api/aggregations/care-delivery?${query}`

    getJSON({ url })
      .then((data) => {
        if (!isCurrent) return
        setData(data.result)
        setIsLoading(false)
      })
      .catch(() => {
        // Without this the screen keeps `data` null, and the table's `!data`
        // guard renders its skeleton forever.
        if (!isCurrent) return
        setHasError(true)
        setIsLoading(false)
      })

    return () => {
      isCurrent = false
    }
  }, [to, from, categoryId])

  return (
    <div style={styles.wrapper}>
      <HeaderSection title={t('reports.careDelivery.title')} />
      <CareDeliveryFilters
        categories={categories}
        to={to}
        from={from}
        setCategoryId={setCategoryId}
        setToDate={setToDate}
        setFromDate={setFromDate}
      />
      {hasError ? (
        <EmptyState
          title={t('reports.careDelivery.errorTitle')}
          description={t('reports.careDelivery.errorDescription')}
          icon={<CrashIcon />}
        />
      ) : (
        <CareDeliveryTable
          isLoading={isLoading}
          // @ts-ignore
          data={data}
          items={items}
          categories={categories}
          to={to}
          from={from}
          // @ts-ignore
          categoryId={categoryId}
          preferredPeopleNominative={preferredPeopleNominative}
        />
      )}
    </div>
  )
}

const styles = {
  wrapper: {
    maxWidth: sizes.fullWidthScreen,
    minHeight: 800,
    margin: '0 auto',
    padding: '24px'
  }
}

const mapStateToProps = (state: any) => ({
  categories: _.orderBy(state.data['log-categories'], 'name'),
  items: _.orderBy(state.data['log-category-items'], 'name'),
  preferredPeopleNominative:
    state.global?.home?.preferredPeopleNominative?.replace(/s$/, '')
})

export default connect(mapStateToProps)(CareDeliveryScreen)
